// One component for every icon in the interface (PLAN-UI §28.1).
//
// **A call site names an icon; it never names a file.** That is the whole
// point: the registry behind this is kit-internal, so swapping the set — a
// different game-icons pick, a commissioned pack, a Material glyph for a
// Material one — is an edit to `icons/paths.ts` and nothing else. The old
// interface drew glyphs (`▐▐`, `♟`, `⚑`) inline in the markup, which is the
// version of this where swapping the set means grepping the app.
//
// **Inline `<svg>` and `fill="currentColor"`, which is why there is no colour
// prop.** An icon takes the colour of the text it sits in, so a button's hover,
// a disabled tile and a refusal pill each recolour their icon by recolouring
// themselves. A `color` prop here would be a second way to say it, and the two
// would disagree — the icon inside a hovered button being the one place it
// showed.
//
// **`aria-hidden` by default.** Nearly every icon in this app sits beside its
// own label or inside a button that already has one, and announcing it a second
// time is noise. `label` is the opt-out, for an icon that *is* the control's
// only name — the pause button, the tool rail.

import { ICON_PATHS, type IconName } from "./icons/paths";

export type { IconName };

/** 20px is the interface's icon size — the tool rail, tiles and pills. */
const DEFAULT_SIZE = 20;

export interface IconProps {
  name: IconName;
  /** Square, in px. */
  size?: number;
  /**
   * The accessible name, when the icon is the control's only name. Absent, the
   * icon is decorative and hidden from assistive technology.
   */
  label?: string;
  className?: string;
}

export function Icon({ name, size = DEFAULT_SIZE, label, className }: IconProps) {
  const icon = ICON_PATHS[name];
  // An array is drawn as several paths — no entry needs it yet, but a
  // multi-path source would otherwise have to be flattened by hand into one
  // `d`, which changes fill-rule behaviour where subpaths overlap.
  const paths = typeof icon.d === "string" ? [icon.d] : icon.d;

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={icon.viewBox}
      fill="currentColor"
      // `block` because an inline SVG sits on the text baseline and leaves a
      // few px of descender gap under it, which shows as a tile that is not
      // quite centred.
      style={{ display: "block", flexShrink: 0 }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
